import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

/**
 * Corner popup toasts (Ctrl+S, send, workspace switches, etc.).
 * Titlebar notifications use notificationStore — unaffected by this flag.
 */
export const POPUP_TOASTS_ENABLED = false;

/** Default visibility — short enough that rapid actions (e.g. env switches) don’t clutter the stack */
const DEFAULT_TOAST_MS = 2200;
const MAX_VISIBLE_TOASTS = 5;

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, toast.duration ?? DEFAULT_TOAST_MS);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onClose]);
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500 flex-shrink-0" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-yellow-500 flex-shrink-0" />;
      default:
        return <Info size={16} className="text-blue-500 flex-shrink-0" />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50';
      case 'error':
        return 'bg-red-50';
      case 'warning':
        return 'bg-yellow-50';
      default:
        return 'bg-blue-50';
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      default:
        return 'text-blue-800';
    }
  };
  return (
    <div className={`flex items-center space-x-2 p-3 rounded-md shadow-lg backdrop-blur-sm animate-slideIn ${getBackgroundColor()} max-w-xs`}>
      {getIcon()}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <h4 className={`font-medium text-xs ${getTextColor()} truncate`}>
            {toast.title}
          </h4>
        )}
        <p className={`text-xs ${getTextColor()} ${toast.title ? 'mt-0.5' : ''} truncate`}>
          {toast.message}
        </p>
      </div>
      <button
        onClick={() => onClose(toast.id)}
        className={`p-0.5 rounded-sm bg-none transition-colors flex-shrink-0 ${
          toast.type === 'success' ? 'hover:bg-green-200' :
          toast.type === 'error' ? 'hover:bg-red-200' :
          toast.type === 'warning' ? 'hover:bg-yellow-200' :
          'hover:bg-blue-200'
        }`}
      >
        <X size={14} className="text-gray-500" />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, onClose }) => {
  if (!POPUP_TOASTS_ENABLED || !toasts || toasts.length === 0) return null;

  return (
    <div className="toast-container space-y-2 max-w-xs w-auto">
      {toasts.map(toast => (
        <div key={toast.id}>
          <Toast toast={toast} onClose={onClose} />
        </div>
      ))}
    </div>
  );
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    if (!POPUP_TOASTS_ENABLED) return;

    const id = Date.now().toString();
    const newToast = {
      id,
      type: 'info',
      duration: DEFAULT_TOAST_MS,
      ...toast
    };
    setToasts((prev) => {
      const next = [...prev, newToast];
      if (next.length <= MAX_VISIBLE_TOASTS) return next;
      return next.slice(next.length - MAX_VISIBLE_TOASTS);
    });
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (message, options = {}) => {
    addToast({ ...options, message, type: 'success' });
  };

  const error = (message, options = {}) => {
    addToast({ ...options, message, type: 'error' });
  };

  const warning = (message, options = {}) => {
    addToast({ ...options, message, type: 'warning' });
  };

  const info = (message, options = {}) => {
    addToast({ ...options, message, type: 'info' });
  };
  const showToast = (message, type = 'info', options = {}) => {
    addToast({ ...options, message, type });
  };

  return {
    toasts,
    removeToast,
    showToast,
    success,
    error,
    warning,
    info
  };
};

export { ToastContainer, useToast };
